import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  Field,
  CheckboxGrid,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  checkboxClass,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getRnDischargeNonVisitForm,
  updateRnDischargeNonVisitForm,
} from "../../../../../services/patientService";
import {
  DoctorIcon,
  FileCheckIcon,
  HeartPulseIcon,
  HelpingIcon,
  LogOutIcon,
  MedicalNotesIcon,
  NurseIcon,
  PillIcon,
  ShieldCheckIcon,
  TargetIcon,
  UserIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  RN_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import { USER_ROLES } from "../../../../../constants";
import {
  CAREGIVER_COMM_OPTIONS,
  COGNITIVE_STATUS_OPTIONS,
  DISCHARGE_DISPOSITION_OPTIONS,
  DISCHARGE_REASON_OPTIONS,
  DISCIPLINE_OPTIONS,
  DOCUMENTATION_REVIEW_OPTIONS,
  GENERAL_CONDITION_OPTIONS,
  MEDICATION_RECONCILE_OPTIONS,
  MOBILITY_STATUS_OPTIONS,
  NON_VISIT_REASON_OPTIONS,
  OASIS_STATUS_OPTIONS,
  OASIS_SUBMISSION_OPTIONS,
  PAIN_STATUS_OPTIONS,
  PHYSICIAN_NOTIFY_OPTIONS,
  PLAN_OF_CARE_GOAL_OPTIONS,
  RESPIRATORY_STATUS_OPTIONS,
  TRANSITION_INFO_OPTIONS,
  emptyRnDischargeNonVisitPayload,
  toggleInList,
  today,
} from "./rnDischargeNonVisitContent";
import NurseNotesCommonHeader from "./NurseNotesCommonHeader";
import WoundPresentSection from "./WoundPresentSection";
import { applyWoundAssessmentPayload } from "./woundAssessmentContent";
import {
  normalizeCommonHeaderState,
  resolveNurseNotesCommonHeader,
  withNormalizedCommonHeader,
} from "./nurseNotesCommonHeaderUtils";
import {
  TIME_OPTIONS_15,
  assertNurseVisitMetaForFlag,
  buildNurseVisitMetaProps,
  pickNurseVisitMeta,
} from "./nurseVisitCommon";

const DOC_DATE_MAX = new Date().toISOString().slice(0, 10);

const ATTESTATION_TEXT =
  "I certify that the information provided in this non-visit discharge documentation accurately reflects the patient's status and the circumstances surrounding their discharge from home health services to the best of my knowledge, based on the clinical record and available information.";

/** Every payload field that holds a checkbox-group selection. */
const ARRAY_KEYS = [
  "nonVisitReasons",
  "discipline",
  "dischargeReasons",
  "medicationReconcile",
  "physicianNotifyFlags",
  "caregiverCommFlags",
  "transitionInfo",
  "oasisSubmission",
  "documentationReview",
];

const emptyValues = () => ({
  ...emptyRnDischargeNonVisitPayload(),
  documentationDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const DateFieldInput = ({ name, value, canEdit, printMode, onChange, max }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};


const RadioRow = ({ options, value, canEdit, onChange, name, stacked = false }) => (
  <div
    className={
      stacked
        ? "flex flex-col gap-1.5"
        : "flex flex-wrap items-center gap-x-4 gap-y-1.5"
    }
  >
    {options.map((opt) => (
      <label
        key={opt}
        className={`inline-flex items-start gap-1.5 text-[12px] text-slate-700 ${
          canEdit ? "cursor-pointer" : "cursor-default"
        }`}
      >
        <input
          type="radio"
          name={name}
          className="mt-0.5 h-3.5 w-3.5 border-slate-300 text-indigo-500 focus:ring-indigo-200"
          checked={value === opt}
          disabled={!canEdit}
          onChange={() => onChange(opt)}
        />
        <span>{opt}</span>
      </label>
    ))}
  </div>
);

RadioRow.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  stacked: PropTypes.bool,
};

const StatusRadioField = ({ label, name, options, value, canEdit, onChange }) => (
  <div className="rounded-md border border-slate-200 bg-white px-2.5 py-2">
    <SubHeading>{label}</SubHeading>
    <RadioRow
      name={name}
      options={options}
      value={value}
      canEdit={canEdit}
      onChange={onChange}
    />
  </div>
);

StatusRadioField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const TimeSelect = ({ name, value, canEdit, onChange }) => (
  <select
    name={name}
    className={fieldClass}
    value={value || ""}
    disabled={!canEdit}
    onChange={(e) => onChange(e.target.value)}
  >
    <option value="">Select time…</option>
    {TIME_OPTIONS_15.map((opt) => (
      <option key={opt} value={opt}>
        {opt}
      </option>
    ))}
  </select>
);

TimeSelect.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const RnDischargeNonVisitForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.lastName, patient?.firstName],
  );

  const normalizeLoadedPayload = useCallback((loadedPayload) => {
    const base = emptyRnDischargeNonVisitPayload();
    const merged = { ...base, ...loadedPayload };

    ARRAY_KEYS.forEach((key) => {
      merged[key] = Array.isArray(loadedPayload[key]) ? loadedPayload[key] : [];
    });

    merged.commonHeader = normalizeCommonHeaderState(
      loadedPayload.commonHeader || base.commonHeader,
    );

    merged.caregiverUnderstood = Boolean(loadedPayload.caregiverUnderstood);
    merged.transitionHandoffProvided = Boolean(
      loadedPayload.transitionHandoffProvided,
    );

    applyWoundAssessmentPayload(merged, merged, {
      mrn: patient?.mrn,
    });

    return merged;
  }, [patient?.mrn]);

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      setValues({
        ...normalizeLoadedPayload(loadedPayload),
        ...pickNurseVisitMeta(loadedPayload, data),
        documentationDate:
          data.documentationDate || loadedPayload.documentationDate || today(),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      });
      resetSignatures();
    },
    [normalizeLoadedPayload, patientName, resetSignatures],
  );

  /** Episode extras used once on first open / empty saved fields. */
  const buildEpisodePrefill = useCallback(() => {
    const episodes = Array.isArray(patient?.episodes) ? patient.episodes : [];
    const latestEpisode = episodes[0];
    const soc = latestEpisode?.socDate || latestEpisode?.startDate || "";
    const certTo = latestEpisode?.endDate || latestEpisode?.toDate || "";
    const isNurse =
      role === USER_ROLES.RN || role === USER_ROLES.LVN;
    const clinicianName = isNurse
      ? [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(" ")
      : "";

    return {
      ...emptyRnDischargeNonVisitPayload(),
      socDate: soc ? formatDateForInput(soc) : "",
      certPeriodFrom: soc ? formatDateForInput(soc) : "",
      certPeriodTo: certTo ? formatDateForInput(certTo) : "",
      clinicianName,
      documentationDate: today(),
      dischargeDate: today(),
      attestationDate: today(),
      attestationClinicianName: clinicianName,
    };
  }, [patient, role, currentUser]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveNurseNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Nurse Discharge (Non-Visit)",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            documentationDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: {
              ...buildEpisodePrefill(),
              commonHeader,
            },
          });
          return;
        }

        const res = await getRnDischargeNonVisitForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const episodePrefill = buildEpisodePrefill();
        const commonHeader = await resolveNurseNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: {
            ...existingPayload,
            socDate: existingPayload.socDate || episodePrefill.socDate || "",
            certPeriodFrom:
              existingPayload.certPeriodFrom ||
              episodePrefill.certPeriodFrom ||
              "",
            certPeriodTo:
              existingPayload.certPeriodTo || episodePrefill.certPeriodTo || "",
            clinicianName:
              existingPayload.clinicianName ||
              episodePrefill.clinicianName ||
              "",
            commonHeader,
          },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        RN_FORM_EDITOR_ROLES,
        "View only — only RN/LVN can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      documentationDate,
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      ...rnFields
    } = values;

    const nestedPayload = withNormalizedCommonHeader({
      ...rnFields,
      documentationDate: documentationDate
        ? formatDateForInput(documentationDate)
        : "",
      dischargeDate: values.dischargeDate
        ? formatDateForInput(values.dischargeDate)
        : "",
      socDate: values.socDate ? formatDateForInput(values.socDate) : "",
      certPeriodFrom: values.certPeriodFrom
        ? formatDateForInput(values.certPeriodFrom)
        : "",
      certPeriodTo: values.certPeriodTo
        ? formatDateForInput(values.certPeriodTo)
        : "",
      physicianNotifyDate: values.physicianNotifyDate
        ? formatDateForInput(values.physicianNotifyDate)
        : "",
      caregiverCommDate: values.caregiverCommDate
        ? formatDateForInput(values.caregiverCommDate)
        : "",
      transitionDate: values.transitionDate
        ? formatDateForInput(values.transitionDate)
        : "",
      attestationDate: values.attestationDate
        ? formatDateForInput(values.attestationDate)
        : "",
      caregiverUnderstood: Boolean(values.caregiverUnderstood),
      transitionHandoffProvided: Boolean(values.transitionHandoffProvided),
    });

    ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      documentationDate: documentationDate
        ? formatDateForInput(documentationDate)
        : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const visitMetaError = assertNurseVisitMetaForFlag(values, flag, {
      requireTimes: false,
    });
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateRnDischargeNonVisitForm,
        filledByUserId,
        scheduleDate: values.documentationDate
          ? formatDateForInput(values.documentationDate)
          : null,
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showDisciplineOther = (values.discipline || []).includes("Other");
  const showNonVisitOther = (values.nonVisitReasons || []).includes("Other");
  const showDischargeReasonOther = (values.dischargeReasons || []).includes(
    "Other",
  );

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Nurse Discharge (Non-Visit)
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Non-visit discharge documentation for skilled nursing home health
            services.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          caregiverSlot={caregiverSlot}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn || values.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              values.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          dateLabel="Date"
          dateValue={displayDateUS(values.documentationDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Date
                </div>
                <InputDatePicker
                  name="documentationDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.documentationDate) || ""}
                  onChange={(event) =>
                    setValue("documentationDate", event.target.value)
                  }
                  max={DOC_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildNurseVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <NurseNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />


        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="space-y-3 min-w-0">
            <Section icon={LogOutIcon} title="7. Discharge Information">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
                <Field label="Discharge Date">
                  <DateFieldInput
                    name="dischargeDate"
                    value={values.dischargeDate}
                    canEdit={canEdit}
                    printMode={printMode}
                    onChange={(v) => setValue("dischargeDate", v)}
                    max={DOC_DATE_MAX}
                  />
                </Field>
                <Field label="Discharge Time">
                  <TimeSelect
                    name="dischargeTime"
                    value={values.dischargeTime}
                    canEdit={canEdit}
                    onChange={(v) => setValue("dischargeTime", v)}
                  />
                </Field>
              </div>

              <div className="mt-3">
                <SubHeading>Discipline</SubHeading>
                <CheckboxGrid
                  options={DISCIPLINE_OPTIONS}
                  values={values.discipline}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("discipline", opt)}
                  columns={3}
                />
                {showDisciplineOther && (
                  <div className="mt-2">
                    <Field label="Discipline — Other">
                      <input
                        type="text"
                        className={fieldClass}
                        value={values.disciplineOther}
                        disabled={!canEdit}
                        onChange={(e) =>
                          setValue("disciplineOther", e.target.value)
                        }
                      />
                    </Field>
                  </div>
                )}
              </div>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
                <Field label="Clinician Name">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.clinicianName}
                    disabled={!canEdit}
                    onChange={(e) => setValue("clinicianName", e.target.value)}
                  />
                </Field>
                <Field label="Credentials">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.credentials}
                    disabled={!canEdit}
                    placeholder="RN, LVN…"
                    onChange={(e) => setValue("credentials", e.target.value)}
                  />
                </Field>
              </div>

              <div className="mt-3">
                <SubHeading>Reason for Non-Visit Documentation</SubHeading>
                <CheckboxGrid
                  options={NON_VISIT_REASON_OPTIONS}
                  values={values.nonVisitReasons}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("nonVisitReasons", opt)}
                  columns={2}
                />
                {showNonVisitOther && (
                  <div className="mt-2">
                    <Field label="Other — Specify">
                      <input
                        type="text"
                        className={fieldClass}
                        value={values.nonVisitReasonOther}
                        disabled={!canEdit}
                        onChange={(e) =>
                          setValue("nonVisitReasonOther", e.target.value)
                        }
                      />
                    </Field>
                  </div>
                )}
              </div>

              <div className="mt-3">
                <Field label="Detailed Explanation">
                  <textarea
                    rows={4}
                    className={textareaClass}
                    value={values.nonVisitExplanation}
                    disabled={!canEdit}
                    placeholder="Describe the circumstances requiring non-visit discharge documentation…"
                    onChange={(e) =>
                      setValue("nonVisitExplanation", e.target.value)
                    }
                  />
                </Field>
              </div>
            </Section>

            <Section
              icon={ShieldCheckIcon}
              title="9. Current Patient Status — Based on Available Information"
            >
              <div className="space-y-2">
                <StatusRadioField
                  label="General Condition"
                  name="generalCondition"
                  options={GENERAL_CONDITION_OPTIONS}
                  value={values.generalCondition}
                  canEdit={canEdit}
                  onChange={(v) => setValue("generalCondition", v)}
                />
                <StatusRadioField
                  label="Pain Status"
                  name="painStatus"
                  options={PAIN_STATUS_OPTIONS}
                  value={values.painStatus}
                  canEdit={canEdit}
                  onChange={(v) => setValue("painStatus", v)}
                />
                <StatusRadioField
                  label="Mobility / Functional Status"
                  name="mobilityStatus"
                  options={MOBILITY_STATUS_OPTIONS}
                  value={values.mobilityStatus}
                  canEdit={canEdit}
                  onChange={(v) => setValue("mobilityStatus", v)}
                />
                <StatusRadioField
                  label="Cognitive / Mental Status"
                  name="cognitiveStatus"
                  options={COGNITIVE_STATUS_OPTIONS}
                  value={values.cognitiveStatus}
                  canEdit={canEdit}
                  onChange={(v) => setValue("cognitiveStatus", v)}
                />
                <StatusRadioField
                  label="Respiratory Status"
                  name="respiratoryStatus"
                  options={RESPIRATORY_STATUS_OPTIONS}
                  value={values.respiratoryStatus}
                  canEdit={canEdit}
                  onChange={(v) => setValue("respiratoryStatus", v)}
                />
              </div>
              <div className="mt-3">
                <Field label="Other Concerns">
                  <textarea
                    rows={2}
                    className={textareaClass}
                    value={values.otherConcerns}
                    disabled={!canEdit}
                    placeholder="Additional clinical concerns based on available information…"
                    onChange={(e) => setValue("otherConcerns", e.target.value)}
                  />
                </Field>
              </div>
            </Section>
          </div>

          <div className="space-y-3 min-w-0">
            <Section icon={HelpingIcon} title="8. Discharge Disposition">
              <SubHeading>Where is the patient after discharge?</SubHeading>
              <RadioRow
                name="dischargeDisposition"
                options={DISCHARGE_DISPOSITION_OPTIONS}
                value={values.dischargeDisposition}
                canEdit={canEdit}
                onChange={(v) => setValue("dischargeDisposition", v)}
                stacked
              />
              <div className="mt-3 space-y-2">
                <Field label="Name of Subsequent Provider">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.subsequentProviderName}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("subsequentProviderName", e.target.value)
                    }
                  />
                </Field>
                <Field label="Provider Phone">
                  <input
                    type="tel"
                    className={fieldClass}
                    value={values.subsequentProviderPhone}
                    disabled={!canEdit}
                    placeholder="(000) 000-0000"
                    onChange={(e) =>
                      setValue("subsequentProviderPhone", e.target.value)
                    }
                  />
                </Field>
                <Field label="Provider Address">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.subsequentProviderAddress}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("subsequentProviderAddress", e.target.value)
                    }
                  />
                </Field>
              </div>
            </Section>

            <Section icon={TargetIcon} title="10. Reason for Discharge">
              <SubHeading>Specific Clinical Reason for Discharge (Check All That Apply)</SubHeading>
              <CheckboxGrid
                options={DISCHARGE_REASON_OPTIONS}
                values={values.dischargeReasons}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("dischargeReasons", opt)}
                columns={1}
              />
              {showDischargeReasonOther && (
                <div className="mt-2">
                  <Field label="Other — Specify">
                    <input
                      type="text"
                      className={fieldClass}
                      value={values.dischargeReasonOther}
                      disabled={!canEdit}
                      onChange={(e) =>
                        setValue("dischargeReasonOther", e.target.value)
                      }
                    />
                  </Field>
                </div>
              )}
              <div className="mt-3">
                <Field label="Justification / Details">
                  <textarea
                    rows={3}
                    className={textareaClass}
                    value={values.dischargeJustification}
                    disabled={!canEdit}
                    placeholder="Clinical justification for discharge…"
                    onChange={(e) =>
                      setValue("dischargeJustification", e.target.value)
                    }
                  />
                </Field>
              </div>
            </Section>
          </div>
        </div>

        <Section icon={NurseIcon} title="Wound Assessment (If Applicable)">
          <WoundPresentSection
            values={values}
            canEdit={canEdit}
            printMode={printMode}
            onChange={(key, next) => setValue(key, next)}
            onToggleList={(key, opt) => toggleListValue(key, opt)}
          />
        </Section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="space-y-3 min-w-0">
            <Section icon={TargetIcon} title="11. Plan of Care / Goals">
              <SubHeading>Status of Current Plan-of-Care Goals</SubHeading>
              <RadioRow
                name="planOfCareGoalStatus"
                options={PLAN_OF_CARE_GOAL_OPTIONS}
                value={values.planOfCareGoalStatus}
                canEdit={canEdit}
                onChange={(v) => setValue("planOfCareGoalStatus", v)}
                stacked
              />
              <div className="mt-3 space-y-2">
                <Field label="Unresolved Problems / Needs">
                  <textarea
                    rows={3}
                    className={textareaClass}
                    value={values.unresolvedProblems}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("unresolvedProblems", e.target.value)
                    }
                  />
                </Field>
                <Field label="Recommended Continued Care (If Applicable)">
                  <textarea
                    rows={3}
                    className={textareaClass}
                    value={values.recommendedContinuedCare}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("recommendedContinuedCare", e.target.value)
                    }
                  />
                </Field>
              </div>
            </Section>

            <Section icon={DoctorIcon} title="13. Physician Notification">
              <CheckboxGrid
                options={PHYSICIAN_NOTIFY_OPTIONS}
                values={values.physicianNotifyFlags}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("physicianNotifyFlags", opt)}
                columns={1}
              />
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
                <Field label="Date">
                  <DateFieldInput
                    name="physicianNotifyDate"
                    value={values.physicianNotifyDate}
                    canEdit={canEdit}
                    printMode={printMode}
                    onChange={(v) => setValue("physicianNotifyDate", v)}
                    max={DOC_DATE_MAX}
                  />
                </Field>
                <Field label="Time">
                  <TimeSelect
                    name="physicianNotifyTime"
                    value={values.physicianNotifyTime}
                    canEdit={canEdit}
                    onChange={(v) => setValue("physicianNotifyTime", v)}
                  />
                </Field>
                <Field label="Method">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.physicianNotifyMethod}
                    disabled={!canEdit}
                    placeholder="Phone, Fax, EMR…"
                    onChange={(e) =>
                      setValue("physicianNotifyMethod", e.target.value)
                    }
                  />
                </Field>
                <Field label="Person Contacted">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.physicianNotifyPerson}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("physicianNotifyPerson", e.target.value)
                    }
                  />
                </Field>
              </div>
              <div className="mt-3">
                <Field label="Response / Orders Received">
                  <textarea
                    rows={3}
                    className={textareaClass}
                    value={values.physicianNotifyResponse}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("physicianNotifyResponse", e.target.value)
                    }
                  />
                </Field>
              </div>
            </Section>

            <Section icon={HeartPulseIcon} title="15. Transition / Handoff">
              <label
                className={`flex items-center gap-2 text-[12px] text-slate-700 rounded-md border px-2.5 py-2 ${
                  canEdit ? "cursor-pointer" : "cursor-default"
                } ${
                  values.transitionHandoffProvided
                    ? "border-indigo-300 bg-indigo-50/60"
                    : "border-slate-200 bg-white"
                }`}
              >
                <input
                  type="checkbox"
                  className={checkboxClass}
                  checked={Boolean(values.transitionHandoffProvided)}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("transitionHandoffProvided", e.target.checked)
                  }
                />
                <span>Transition/Handoff communication provided</span>
              </label>

              <div className="mt-3">
                <SubHeading>Information Communicated</SubHeading>
                <CheckboxGrid
                  options={TRANSITION_INFO_OPTIONS}
                  values={values.transitionInfo}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("transitionInfo", opt)}
                  columns={2}
                />
              </div>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-2">
                <Field label="Date">
                  <DateFieldInput
                    name="transitionDate"
                    value={values.transitionDate}
                    canEdit={canEdit}
                    printMode={printMode}
                    onChange={(v) => setValue("transitionDate", v)}
                    max={DOC_DATE_MAX}
                  />
                </Field>
                <Field label="Method">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.transitionMethod}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("transitionMethod", e.target.value)
                    }
                  />
                </Field>
                <Field label="Recipient">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.transitionRecipient}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("transitionRecipient", e.target.value)
                    }
                  />
                </Field>
              </div>
            </Section>
          </div>

          <div className="space-y-3 min-w-0">
            <Section icon={PillIcon} title="12. Medication / Treatment Information">
              <CheckboxGrid
                options={MEDICATION_RECONCILE_OPTIONS}
                values={values.medicationReconcile}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("medicationReconcile", opt)}
                columns={1}
              />
              <div className="mt-3 space-y-2">
                <Field label="Medication Concerns / Issues Noted Prior to Discharge">
                  <textarea
                    rows={3}
                    className={textareaClass}
                    value={values.medicationConcerns}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("medicationConcerns", e.target.value)
                    }
                  />
                </Field>
                <Field label="Outstanding Orders or Treatments at Time of Discharge">
                  <textarea
                    rows={3}
                    className={textareaClass}
                    value={values.outstandingOrders}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("outstandingOrders", e.target.value)
                    }
                  />
                </Field>
              </div>
            </Section>

            <Section
              icon={UserIcon}
              title="14. Patient / Caregiver Communication"
            >
              <CheckboxGrid
                options={CAREGIVER_COMM_OPTIONS}
                values={values.caregiverCommFlags}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("caregiverCommFlags", opt)}
                columns={1}
              />
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
                <Field label="Date">
                  <DateFieldInput
                    name="caregiverCommDate"
                    value={values.caregiverCommDate}
                    canEdit={canEdit}
                    printMode={printMode}
                    onChange={(v) => setValue("caregiverCommDate", v)}
                    max={DOC_DATE_MAX}
                  />
                </Field>
                <Field label="Time">
                  <TimeSelect
                    name="caregiverCommTime"
                    value={values.caregiverCommTime}
                    canEdit={canEdit}
                    onChange={(v) => setValue("caregiverCommTime", v)}
                  />
                </Field>
                <Field label="Method">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.caregiverCommMethod}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("caregiverCommMethod", e.target.value)
                    }
                  />
                </Field>
                <Field label="Person Contacted">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.caregiverCommPerson}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("caregiverCommPerson", e.target.value)
                    }
                  />
                </Field>
              </div>
              <div className="mt-3">
                <Field label="Instructions Communicated">
                  <textarea
                    rows={3}
                    className={textareaClass}
                    value={values.caregiverInstructions}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("caregiverInstructions", e.target.value)
                    }
                  />
                </Field>
              </div>
              <label
                className={`mt-3 flex items-center gap-2 text-[12px] text-slate-700 rounded-md border px-2.5 py-2 ${
                  canEdit ? "cursor-pointer" : "cursor-default"
                } ${
                  values.caregiverUnderstood
                    ? "border-indigo-300 bg-indigo-50/60"
                    : "border-slate-200 bg-white"
                }`}
              >
                <input
                  type="checkbox"
                  className={checkboxClass}
                  checked={Boolean(values.caregiverUnderstood)}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("caregiverUnderstood", e.target.checked)
                  }
                />
                <span>
                  Patient/Caregiver verbalized understanding of instructions
                </span>
              </label>
            </Section>

            <Section icon={FileCheckIcon} title="16. OASIS / CMS Assessment Review">
              <SubHeading>OASIS Status</SubHeading>
              <RadioRow
                name="oasisStatus"
                options={OASIS_STATUS_OPTIONS}
                value={values.oasisStatus}
                canEdit={canEdit}
                onChange={(v) => setValue("oasisStatus", v)}
              />
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
                <Field label="Assessment Status">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.oasisAssessmentStatus}
                    disabled={!canEdit}
                    placeholder="e.g. Completed, Pending…"
                    onChange={(e) =>
                      setValue("oasisAssessmentStatus", e.target.value)
                    }
                  />
                </Field>
                <Field label="Time Point">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.oasisTimePoint}
                    disabled={!canEdit}
                    placeholder="e.g. Transfer, Discharge…"
                    onChange={(e) => setValue("oasisTimePoint", e.target.value)}
                  />
                </Field>
              </div>
              <div className="mt-3">
                <SubHeading>Submission</SubHeading>
                <CheckboxGrid
                  options={OASIS_SUBMISSION_OPTIONS}
                  values={values.oasisSubmission}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("oasisSubmission", opt)}
                  columns={2}
                />
              </div>
              <div className="mt-3">
                <Field label="Comments">
                  <textarea
                    rows={3}
                    className={textareaClass}
                    value={values.oasisComments}
                    disabled={!canEdit}
                    onChange={(e) => setValue("oasisComments", e.target.value)}
                  />
                </Field>
              </div>
            </Section>
          </div>
        </div>

        <Section icon={FileCheckIcon} title="17. Discharge Documentation Review">
          <CheckboxGrid
            options={DOCUMENTATION_REVIEW_OPTIONS}
            values={values.documentationReview}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("documentationReview", opt)}
            columns={3}
          />
        </Section>

        <Section icon={MedicalNotesIcon} title="18. Final Clinical Summary">
          <Field label="Clinical Summary">
            <textarea
              rows={6}
              className={textareaClass}
              value={values.finalClinicalSummary}
              disabled={!canEdit}
              placeholder="Summarize the clinical course, discharge circumstances, and relevant follow-up…"
              onChange={(e) => setValue("finalClinicalSummary", e.target.value)}
            />
          </Field>
        </Section>

        <Section icon={ShieldCheckIcon} title="19. Nurse Attestation">
          <p className="text-[12px] leading-relaxed text-slate-700 rounded-md border border-indigo-100 bg-indigo-50/40 px-3 py-2.5">
            {ATTESTATION_TEXT}
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-2">
            <Field label="Clinician Name & Credentials">
              <input
                type="text"
                className={fieldClass}
                value={values.attestationClinicianName}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("attestationClinicianName", e.target.value)
                }
              />
            </Field>
            <Field label="Date">
              <DateFieldInput
                name="attestationDate"
                value={values.attestationDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("attestationDate", v)}
                max={DOC_DATE_MAX}
              />
            </Field>
            <Field label="Time">
              <TimeSelect
                name="attestationTime"
                value={values.attestationTime}
                canEdit={canEdit}
                onChange={(v) => setValue("attestationTime", v)}
              />
            </Field>
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="Clinician Authentication"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

RnDischargeNonVisitForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  /** Present when opened from the picker before any server row exists. */
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  /** Called with the new clinical-form id after the first draft save. */
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default RnDischargeNonVisitForm;
